import { observer } from 'mobx-react-lite'
import { Button, Divider, Label, PhoneWithLabelInput, Typography, Warning } from 'shared/ui'
import { makeTextFieldHighlightLimit, TextField } from 'shared/ui/TextField'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { CHAR_MAX_LENGTH } from 'shared/constants/limits'
import { useMemberContext } from 'pages/settings/pages/members/context/memberContext'

import styles from './styles.module.scss'

export const MemberProfile = observer(() => {
  const {
    member,
    onSaveMemberProfile,
    handleTimezone,
    isOwner,
    handleLogoutAllDevices,
    handleDeleteMember,
    handleResetPassword,
    isMyProfile,
    memberProfileStore: {
      firstName,
      setFirstName,
      limitFirstNameReached,
      setLimitFirstNameReached,
      lastName,
      setLastName,
      limitLastNameReached,
      setLimitLastNameReached,
      email,
      timezone,
      phoneInputStore,
      timezoneItems,
    },
  } = useMemberContext()

  const handleBlur = () => {
    if (limitFirstNameReached || limitLastNameReached) return

    void onSaveMemberProfile()
  }

  if (!member) return null

  return (
    <div className={styles.wrapper}>
      <div className={styles.formWrap}>
        <div className={styles.inputWrap}>
          <Label
            label={{
              text: 'First name',
            }}
          />
          <TextField
            onBlur={handleBlur}
            InputProps={{
              placeholder: 'First name',
            }}
            disabled={isOwner}
            size='medium'
            variant='stroke'
            value={firstName}
            onChange={setFirstName}
            mainActions={[
              makeTextFieldHighlightLimit({
                limit: CHAR_MAX_LENGTH,
                onChange: setLimitFirstNameReached,
              }),
            ]}
          />
          {limitFirstNameReached && <Warning text={`${CHAR_MAX_LENGTH}-character limit reached`} />}
        </div>
        <div className={styles.inputWrap}>
          <Label
            label={{
              text: 'Last name',
            }}
          />
          <TextField
            onBlur={handleBlur}
            InputProps={{
              placeholder: 'Last name',
            }}
            variant='stroke'
            size='medium'
            disabled={isOwner}
            value={lastName}
            onChange={setLastName}
            mainActions={[
              makeTextFieldHighlightLimit({
                limit: CHAR_MAX_LENGTH,
                onChange: setLimitLastNameReached,
              }),
            ]}
          />
          {limitLastNameReached && <Warning text={`${CHAR_MAX_LENGTH}-character limit reached`} />}
        </div>
        <div className={styles.inputWrap}>
          <Label
            label={{
              text: 'Email',
            }}
          />
          <TextField
            InputProps={{
              placeholder: 'Email',
            }}
            variant='stroke'
            size='medium'
            value={email}
            disabled
          />
        </div>
        <div className={styles.inputWrap}>
          <Label
            label={{
              text: 'Phone number',
            }}
          />
          <PhoneWithLabelInput
            item={phoneInputStore.country}
            items={phoneInputStore.countries}
            number={phoneInputStore.number}
            hideLabel
            disabled
          />
        </div>
        <div className={styles.inputWrap}>
          <SelectField
            boxWidth={'100%'}
            labelText={'Timezone'}
            value={timezone}
            size='medium'
            setValue={handleTimezone}
            disabled={isOwner}
            dropdownProps={{
              propsDropdownList: {
                withGrouping: true,
                dropdownGroupProps: {
                  isScroll: false,
                },
              },
            }}
            items={timezoneItems}
          />
        </div>
      </div>
      <Divider sx={{ margin: '0px 4px', borderColor: 'var(--border-neutral-default)' }} />
      <div className={styles.actionsWrap}>
        <div className={styles.action}>
          <div className={styles.label}>
            <Typography
              variant='body-md-medium'
              color='var(--content-primary-primary)'
              ariaLabel='Reset password title'
            >
              Password
            </Typography>
            <Typography
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
              ariaLabel='Reset password description'
            >
              Send password reset email
            </Typography>
          </div>
          <Button
            disabled={isOwner}
            contained='secondary'
            size='medium'
            onClick={handleResetPassword}
            icon='refresh'
            text='Reset password'
          />
        </div>
        <div className={styles.action}>
          <div className={styles.label}>
            <Typography
              variant='body-md-medium'
              color='var(--content-primary-primary)'
              ariaLabel='Log out member title'
            >
              Log out member
            </Typography>
            <Typography
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
              ariaLabel='Log out member description'
            >
              Log out of all active sessions on all devices
            </Typography>
          </div>
          <Button
            contained='secondary'
            size='medium'
            onClick={handleLogoutAllDevices}
            icon='logOut'
            text='Log out all devices'
          />
        </div>
      </div>
      {!isOwner && !isMyProfile && (
        <>
          <Divider sx={{ margin: '0px 4px', borderColor: 'var(--border-neutral-default)' }} />
          <div className={styles.actionsWrap}>
            <div className={styles.action}>
              <div className={styles.label}>
                <Typography
                  variant='body-md-medium'
                  color='var(--content-primary-primary)'
                  ariaLabel='Delete member title'
                >
                  Delete member
                </Typography>
                <Typography
                  variant='body-md-regular'
                  color='var(--content-primary-tertiary)'
                  ariaLabel='Delete member description'
                >
                  Delete the account and remove access from the workspace
                </Typography>
              </div>
              <Button
                contained='secondary'
                size='medium'
                onClick={handleDeleteMember}
                icon='delete'
                text='Delete member'
                intent='destructive'
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
})
